import { z } from 'zod';
import { counter } from './counts.js';
import { segmentDefinitionSchema } from './segments.js';
import { emailSchema } from './signUp.js';
/**
 * The contacts list.
 *
 * `suppressedFor` is part of the contract rather than something the screen
 * infers, because "subscribed" and "will actually receive mail" are different
 * questions and only the server can answer the second.
 */
/**
 * Where a contact stands with this organisation.
 *
 * Exported and shared, because it is asked for in four places — a row, a
 * duplicate, an imported row and the form that adds one — and four copies of
 * the same three words is four places for one to gain a state the database
 * cannot store.
 */
export const contactStatusSchema = z.enum(['subscribed', 'unsubscribed', 'cleaned']);
const suppressionReasonSchema = z.enum(['unsubscribed', 'hard_bounce', 'complaint', 'manual']);
export const contactSummarySchema = z.object({
    contactId: z.string(),
    email: emailSchema,
    name: z.string(),
    status: contactStatusSchema,
    /** Absent when nothing suppresses this address. */
    suppressedFor: suppressionReasonSchema.optional(),
    createdAt: z.iso.datetime(),
    /**
     * The segments this contact was connected to by hand, in name order.
     *
     * Only the connected ones. A contact the rules of a segment match is in that
     * segment's audience too and is deliberately not listed: showing it would
     * mean running every segment's rules against every row of every page, and a
     * chip that cannot be removed is a chip that reads as broken.
     */
    segments: z.array(z.object({ segmentId: z.string(), name: z.string() })),
    /** Empty is "not given", which is what the column holds. */
    company: z.string(),
    /** Values for declared fields, keyed by field key, so they can be columns. */
    custom: z.record(z.string().max(40), z.string()),
});
/**
 * Which column a board is ordered by.
 *
 * The column is one of a fixed set rather than a free string: it becomes part
 * of an `order by`, and the only safe way to let a caller choose one is to
 * choose from names the server already knows. A declared field is named by its
 * key, which is bound as a parameter into a jsonb lookup.
 */
export const contactSortSchema = z.object({
    column: z.enum(['name', 'email', 'company', 'status', 'created', 'field']),
    key: z.string().max(40).optional(),
    direction: z.enum(['asc', 'desc']),
});
/**
 * Which view the page is of.
 *
 * **A view is not a segment.** A view is a way of looking at this list; a
 * segment is an audience a campaign is sent to. They share the rule vocabulary
 * and nothing else.
 *
 * A view can be deleted while somebody has its URL open. `view-gone` is said
 * rather than quietly falling back to everybody: on a screen whose next action
 * is "suppress these", widening the selection without a word is the worst thing
 * it could do — so a view that is gone matches nobody, and the screen explains
 * itself.
 */
export const contactViewSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('everyone') }),
    z.object({
        kind: z.literal('view'),
        viewId: z.string(),
        name: z.string(),
        /** So the board draws the view as it was saved rather than as it opened. */
        sort: contactSortSchema.nullable(),
        hidden: z.array(z.string()),
    }),
    z.object({ kind: z.literal('view-gone') }),
]);
export const contactPageSchema = z.object({
    contacts: z.array(contactSummarySchema),
    /** Matching the filter, not the page. The screen shows both numbers. */
    total: counter,
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    view: contactViewSchema,
});
/**
 * Everything known about one contact.
 *
 * The custom values carry their label as well as their key, because the key
 * is plumbing — a screen showing `renewal_date` is a screen showing how the
 * thing is stored rather than what it is.
 */
export const contactDetailSchema = z.object({
    contactId: z.string(),
    email: emailSchema,
    name: z.string(),
    status: contactStatusSchema,
    company: z.string().nullable(),
    custom: z.array(z.object({ key: z.string(), label: z.string(), value: z.string() })),
    /** Why nothing will reach this address, and since when. */
    suppressedFor: suppressionReasonSchema.nullable(),
    suppressedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    /** Oldest first, because it is a story. */
    consent: z.array(z.object({ type: z.string(), source: z.string(), occurredAt: z.iso.datetime() })),
    sends: z.array(z.object({
        campaignName: z.string().nullable(),
        status: z.string(),
        reason: z.string().nullable(),
        createdAt: z.iso.datetime(),
    })),
});
/**
 * A board's whole question, in a body rather than a url.
 *
 * `GET /api/contacts` answers the simple case and stays: it is part of the
 * surface an api key can call, and a query string is the right shape for a
 * search and a page. A *filter* is a rule tree, and two things make it a body
 * instead. It does not fit a url at any useful size, and its values are
 * somebody's data — an address fragment in a url is an address fragment in
 * browser history and in every log between here and the server.
 *
 * Both go through one use case, so the two cannot answer differently.
 */
export const contactQueryRequestSchema = z.object({
    search: z.string().max(200).default(''),
    /** A saved view's id. Absent is every contact. */
    view: z.string().optional(),
    /** Extra rules on top of the view, in the shape a segment stores. */
    filter: segmentDefinitionSchema.optional(),
    sort: contactSortSchema.optional(),
    page: z.number().int().min(1).default(1),
});
/**
 * Changing what is known about one contact.
 *
 * **The address is not here.** Suppression is keyed on `(org_id, email)` and so
 * is the consent log, so renaming somebody's address would move them out from
 * under their own unsubscribe and their own history in one write, with nothing
 * to say it had happened. Somebody whose address changed is a different
 * contact.
 *
 * Every field is sent, not only the changed ones: the form holds the whole
 * contact, and a partial update is a second shape for one write — the kind that
 * drifts until nobody can say which fields a save actually touches.
 */
export const editContactRequestSchema = z.object({
    name: z.string().max(200),
    company: z.string().max(200),
    status: contactStatusSchema,
    /** Replaces the declared values whole. Unknown keys are dropped server-side. */
    custom: z.record(z.string().max(40), z.string().max(500)),
});
/**
 * Setting one column on a selection of contacts.
 *
 * **One column, not a form.** "Select fifty, change Subscribed to
 * Unsubscribed" is the thing this exists for, and a form that set three columns
 * at once would make "leave this alone" and "set this to empty" the same
 * gesture across two of them.
 *
 * **A tick list, never a filter.** Unlike connecting a segment, which can act
 * on everybody a filter matches, this names the rows. Setting a column on forty
 * thousand people the server resolved after the page was drawn is not something
 * to offer behind one press, and nothing on screen could honestly name who it
 * was about. The same reason deleting is a tick list.
 *
 * An empty list changes nobody: a selection that arrived empty was lost on the
 * way, and rewriting a column across an organisation because a field went
 * missing is a mistake nothing downstream would catch.
 *
 * The address and the name are not among the columns. An address cannot be
 * changed at all, because suppression and the consent log are keyed on it; a
 * name is never one fifty people share.
 */
export const bulkEditRequestSchema = z.object({
    contactIds: z.array(z.string()).min(1).max(1000),
    change: z.discriminatedUnion('column', [
        z.object({ column: z.literal('status'), value: contactStatusSchema }),
        /** Empty is "not given", which is what the column holds for a contact with none. */
        z.object({ column: z.literal('company'), value: z.string().max(200) }),
        /**
         * A declared field, named by its key — one arm rather than one per field,
         * so declaring a column never changes this contract. The key is checked
         * against what the organisation actually declared on the server, exactly as
         * an import and a single edit are.
         *
         * Empty removes the value rather than storing `""`, so "clear this column
         * for these fifty" is expressible and "has no plan" stays one answer.
         */
        z.object({ column: z.literal('field'), key: z.string().max(40), value: z.string().max(500) }),
    ]),
});
/** How many rows were set, which is not how many ids were sent. */
export const bulkEditResponseSchema = z.object({
    changed: counter,
});
/**
 * Deleting contacts.
 *
 * Named rather than filtered, unlike clearing duplicates: this is a tick list
 * somebody made, and every row in it is on screen. An empty list deletes
 * nobody — a selection that arrived empty was lost on the way, and deleting an
 * organisation's contacts because a field went missing is a mistake nothing
 * downstream would catch.
 */
export const deleteContactsRequestSchema = z.object({
    contactIds: z.array(z.string()).min(1).max(1000),
});
/** How many were actually removed, which is not how many were asked for. */
export const deleteContactsResponseSchema = z.object({
    removed: z.number().int().min(0),
});
/**
 * Replacing names that say nothing with ones taken from the address.
 *
 * `placeholder` is the exact name being replaced — "Item", on a monday board
 * whose items are all called that. Blank names are always included, because a
 * contact with no name at all has nothing to lose.
 *
 * **Exact, never a pattern.** A rule that guessed which names are not real
 * would eventually rewrite somebody's actual name, and there is no undo. The
 * caller says the word, so the scope is something a person chose and can see.
 */
export const fixNamesRequestSchema = z.object({
    placeholder: z.string().trim().max(200).optional(),
});
/** How many names were set — not how many matched, and not how many were asked about. */
export const fixNamesResponseSchema = z.object({ changed: counter });
//# sourceMappingURL=contacts.js.map